import {
  CHECK_DELIVERED_BOOKINGS,
  CHECK_DELIVERED_SERVICES,
  CREATED_SERVICE,
  CREATE_SERVICE,
  DISCARD_EXPIRED_NOT_DELIVERED_NOTIFICATIONS,
  ERROR_CREATING_SERVICE,
  EXPIRE_NOT_DELIVERED_NOTIFICATIONS_AFTER_SECONDS,
  RECOVERY_NOT_DELIVERED_BOOKING,
  RECOVERY_NOT_DELIVERED_SERVICE,
  RECOVERY_SERVICE,
  RETRY_CREATE_SERVICE,
  TIME_BEFORE_MARK_AS_NOT_DELIVERED,
  UPDATE_BOOKING_SERVICES,
  UPDATE_LIVE_SERVICES,
} from "@/events/constants";

import ApiClient from "@/api/ApiClient.ts";
import router from "@/router";
import { getUnixTimeInSeconds } from "@/utils/date";
import { encode } from "universal-base64url";
import { UPDATE_HISTORIC_SERVICES } from "../constants";
import audit from "../../modules/audit/utils/services.ts";
import { PAUSED_STATE, SCHEDULE_TYPE_BOOKING_ID, SCHEDULE_TYPE_NOW_ID } from "@/utils/constants.js";

const store = window.store;

const handleCreatedService = (event) => {
  store.dispatch("services/removeEnqueued", event);
  audit.recordCreateService(
    event.payload.data.wgId,
    event.payload.data.centralId,
    event.serviceId,
    event.payload.data,
    event.payload.data.state === PAUSED_STATE ? SCHEDULE_TYPE_BOOKING_ID : SCHEDULE_TYPE_NOW_ID
  );
  if (event.type === "now") {
    store.dispatch("services/addWaitingToBeDelivered", {
      inserted: getUnixTimeInSeconds(),
      token: event.token,
      payload: event.payload,
    });
  }
};

const handleUpdateLiveServices = async (event) => {
  await store.dispatch("home/setTrips", event);
  await store.dispatch("services/removeToBeDelivered", event);
  await store.dispatch("services/removeBookingsToBeDelivered", event);
};

const handleUpdateBookings = (event) => {
  store.dispatch("home/setServices", event);
  store.dispatch(
    "services/addBookingsToBeDelivered",
    event.filter(
      (service) =>
        !service.datePassed && (service.type === 2 || service.type === 3) && service.state !== 4
    )
  );
};

const handleUpdateHistoricServices = (event) => {
  store.dispatch("home/setHistoricTrips", event);
};

const handleErrorCreatingService = ({ event, error, errorCode }) => {
  if (errorCode === 412) {
    return;
  }
  store.dispatch("services/addError", { event, error, errorCode });
};

const handleRetryCreateService = (event) => {
  window.$bus.emit(CREATE_SERVICE, event);
};

const openServiceFormWithService = (event) => {
  const paramId = new Date().getTime();
  const { token, payload } = event;
  const data = encode(JSON.stringify(payload.rawData));
  router.push({
    path: `/services/create/${paramId}?recovery=${token}&data=${data}`,
  });
};

const openServiceFormWithBooking = (event) => {
  const { token, payload } = event;
  const { id, wgId, customer } = payload;
  router.push({
    path: "/services/edit",
    query: {
      id: id,
      wgId: wgId,
      type: 2,
      nameToShow: `Reserva: ${customer.phone}`,
      recovery: token,
    },
  });
};

const handleRecoveryService = (event) => {
  store.dispatch("services/removeEnqueued", event);
  openServiceFormWithService(event);
};

const handleRecoveryNotDeliveredService = (event) => {
  store.dispatch("services/removeNotDeliveredService", event);
  openServiceFormWithService(event);
};

const handleRecoveryNotDeliveredBooking = (event) => {
  store.dispatch("services/removeNotDeliveredBooking", event);
  openServiceFormWithBooking(event);
};

const handleCheckDeliveredBookings = () => {
  const bookingsWaitingToBeDelivered = store.getters["services/bookingsWaitingToBeDelivered"];
  for (let event of Object.values(bookingsWaitingToBeDelivered)) {
    if (getUnixTimeInSeconds() > event.inserted + TIME_BEFORE_MARK_AS_NOT_DELIVERED) {
      store.dispatch("services/addBookingDeliveryError", event);
    }
  }
};

const handleCheckDeliveredServices = () => {
  const servicesWaitingToBeDelivered = store.getters["services/servicesWaitingToBeDelivered"];
  for (let event of Object.values(servicesWaitingToBeDelivered)) {
    if (getUnixTimeInSeconds() > event.inserted + TIME_BEFORE_MARK_AS_NOT_DELIVERED) {
      store.dispatch("services/addServiceDeliveryError", event);
    }
  }
};

const handleDiscardExpiredNotDeliveredNotifications = () => {
  const servicesNotDelivered = store.getters["services/servicesNotDelivered"];
  for (let event of Object.values(servicesNotDelivered)) {
    if (
      getUnixTimeInSeconds() - event.inserted >=
      EXPIRE_NOT_DELIVERED_NOTIFICATIONS_AFTER_SECONDS
    ) {
      store.dispatch("services/removeNotDeliveredService", event);
    }
  }

  const bookingsNotDelivered = store.getters["services/bookingsNotDelivered"];
  for (let event of Object.values(bookingsNotDelivered)) {
    if (
      getUnixTimeInSeconds() - event.inserted >=
      EXPIRE_NOT_DELIVERED_NOTIFICATIONS_AFTER_SECONDS
    ) {
      store.dispatch("services/removeNotDeliveredBooking", event);
    }
  }
};

const handleCreateService = async (event) => {
  const { payload, token } = event;
  await store.dispatch("services/addEnqueued", event);
  try {
    const serviceId = await new ApiClient()
      .post("services/services2", payload.data, {
        headers: {
          "X-Req-Code": token,
        },
        timeout: 15000,
      })
      .then((res) => {
        if (res.status >= 400) {
          throw new Error(`Error creating service with status code ${res.status}.`);
        }
        return res.data;
      });

    window.$bus.emit(CREATED_SERVICE, {
      ...event,
      serviceId,
    });
  } catch (err) {
    console.error(err);
    window.$bus.emit(ERROR_CREATING_SERVICE, {
      event,
      error: err,
      errorResponse: err.response?.data,
      errorCode: err.response?.status,
    });
  }
};

export default {
  async handle(event, payload) {
    switch (event) {
      case CREATE_SERVICE:
        await handleCreateService(payload);
        break;
      case CREATED_SERVICE:
        handleCreatedService(payload);
        break;
      case UPDATE_LIVE_SERVICES:
        handleUpdateLiveServices(payload);
        break;
      case UPDATE_BOOKING_SERVICES:
        handleUpdateBookings(payload);
        break;
      case UPDATE_HISTORIC_SERVICES:
        handleUpdateHistoricServices(payload);
        break;
      case ERROR_CREATING_SERVICE:
        handleErrorCreatingService(payload);
        break;
      case RETRY_CREATE_SERVICE:
        handleRetryCreateService(payload);
        break;
      case RECOVERY_SERVICE:
        handleRecoveryService(payload);
        break;
      case RECOVERY_NOT_DELIVERED_SERVICE:
        handleRecoveryNotDeliveredService(payload);
        break;
      case RECOVERY_NOT_DELIVERED_BOOKING:
        handleRecoveryNotDeliveredBooking(payload);
        break;
      case CHECK_DELIVERED_SERVICES:
        handleCheckDeliveredServices();
        break;
      case CHECK_DELIVERED_BOOKINGS:
        handleCheckDeliveredBookings();
        break;
      case DISCARD_EXPIRED_NOT_DELIVERED_NOTIFICATIONS:
        handleDiscardExpiredNotDeliveredNotifications();
        break;
    }
  },
};
